import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Book, StorytellerOption } from '../../types';
import { getGraphQLClient, GENERATE_WRITING_STYLE_OPTIONS, UPDATE_BOOK } from '../../lib/graphql';
import { captureException } from '../../lib/sentry';
import { Button, AiEnhancedTextarea } from '../common';
import AiGenerateDropdownButton, { AiGenerateAction } from '../common/AiGenerateDropdownButton';
import { AiProcessingAnimation } from '../ai';

interface StorytellerTabProps {
  book: Book;
  onBookChange: (book: Book) => void;
  onNext?: () => void;
}

type ViewMode = 'initial' | 'options' | 'editing';

interface FieldState {
  value: string;
  options: StorytellerOption[];
  viewMode: ViewMode;
  isGenerating: boolean;
  expandedOptions: Set<string>;
}

export default function StorytellerTab({ book, onBookChange, onNext }: StorytellerTabProps) {
  const { t } = useTranslation();

  // Writing style field state
  const [writingStyle, setWritingStyle] = useState<FieldState>({
    value: book.writingStyle || '',
    options: [],
    viewMode: book.writingStyle ? 'editing' : 'initial',
    isGenerating: false,
    expandedOptions: new Set(),
  });

  const [error, setError] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const hasSummary = !!book.summary?.trim();

  // Initialize from book data
  useEffect(() => {
    setWritingStyle((prev) => ({
      ...prev,
      value: book.writingStyle || '',
      viewMode: book.writingStyle ? 'editing' : 'initial',
    }));
  }, [book.writingStyle]);

  const generateOptions = async () => {
    if (!hasSummary) return;

    setWritingStyle((prev) => ({ ...prev, isGenerating: true }));
    setError('');

    try {
      const client = getGraphQLClient();
      const data = await client.request<{
        generateWritingStyleOptions: { options: StorytellerOption[]; errors: string[] };
      }>(GENERATE_WRITING_STYLE_OPTIONS, {
        bookId: book.id,
      });

      if (data.generateWritingStyleOptions.errors.length > 0) {
        setError(data.generateWritingStyleOptions.errors[0]);
      } else if (data.generateWritingStyleOptions.options) {
        setWritingStyle((prev) => ({
          ...prev,
          options: data.generateWritingStyleOptions.options,
          viewMode: 'options',
        }));
      }
    } catch (err) {
      captureException(err, {
        component: 'StorytellerTab',
        action: 'generateWritingStyleOptions',
        bookId: book.id,
      });
      setError(t('common.error'));
    } finally {
      setWritingStyle((prev) => ({ ...prev, isGenerating: false }));
    }
  };

  // AI dropdown action handler for writing style
  const handleWritingStyleAiAction = (action: AiGenerateAction, _instructions?: string) => {
    // All generate/rephrase actions trigger options generation
    if (action === 'generate' || action === 'generateWithInstructions' || action === 'rephrase' || action === 'rephraseWithInstructions') {
      generateOptions();
    }
  };

  const handleSelectOption = (option: StorytellerOption) => {
    setWritingStyle((prev) => ({
      ...prev,
      value: option.content,
      viewMode: 'editing',
    }));
    setHasUnsavedChanges(true);
  };

  const toggleExpandOption = (optionId: string) => {
    setWritingStyle((prev) => {
      const newExpanded = new Set(prev.expandedOptions);
      if (newExpanded.has(optionId)) {
        newExpanded.delete(optionId);
      } else {
        newExpanded.add(optionId);
      }
      return { ...prev, expandedOptions: newExpanded };
    });
  };

  const handleClearField = () => {
    setWritingStyle((prev) => ({
      ...prev,
      value: '',
      viewMode: 'initial',
      options: [],
    }));
    setHasUnsavedChanges(true);
  };

  const handleSave = async (): Promise<boolean> => {
    setError('');

    try {
      const client = getGraphQLClient();
      const data = await client.request<{
        updateBook: { book: Book; errors: string[] };
      }>(UPDATE_BOOK, {
        id: book.id,
        writingStyle: writingStyle.value.trim() || null,
      });

      if (data.updateBook.errors.length > 0) {
        setError(data.updateBook.errors[0]);
        return false;
      } else if (data.updateBook.book) {
        onBookChange(data.updateBook.book);
        setHasUnsavedChanges(false);
        return true;
      }
      return false;
    } catch (err) {
      captureException(err, {
        component: 'StorytellerTab',
        action: 'saveAll',
        bookId: book.id,
      });
      setError(t('common.error'));
      return false;
    }
  };

  const handleNext = async () => {
    setIsSaving(true);
    try {
      // Save if there are unsaved changes
      if (hasUnsavedChanges) {
        const success = await handleSave();
        if (!success) {
          return;
        }
      }
      // Navigate to next step (complete editing)
      if (onNext) {
        onNext();
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Options display component
  const renderOptions = () => (
    <div className="space-y-3">
      <AnimatePresence>
        {writingStyle.options.map((option, index) => {
          const isExpanded = writingStyle.expandedOptions.has(option.id);
          return (
            <motion.div
              key={option.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="border rounded-xl overflow-hidden hover:border-primary-500 transition-all duration-200"
            >
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400 font-semibold">
                    {index + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">{option.title}</h4>
                    <div className="mt-2">
                      <p
                        className={`text-sm text-gray-600 dark:text-gray-400 leading-relaxed ${
                          !isExpanded ? 'line-clamp-2' : ''
                        }`}
                      >
                        {option.content}
                      </p>
                      {option.content.length > 150 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpandOption(option.id);
                          }}
                          className="text-xs text-primary-600 dark:text-primary-400 hover:underline mt-1"
                        >
                          {isExpanded ? t('wizard.showLess') : t('wizard.showMore')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleSelectOption(option)}
                className="w-full px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 bg-gray-50 dark:bg-gray-800/50 hover:bg-primary-50 dark:hover:bg-primary-900/30 border-t border-gray-200 dark:border-gray-700 transition-colors"
              >
                {t('wizard.selectThisVoice')}
              </button>
            </motion.div>
          );
        })}
        {/* Other - free form input option */}
        <motion.div
          key="other"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: writingStyle.options.length * 0.1 }}
          className="border border-dashed rounded-xl overflow-hidden hover:border-primary-500 transition-all duration-200 cursor-pointer"
          onClick={() => setWritingStyle((prev) => ({ ...prev, viewMode: 'editing' }))}
        >
          <div className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                  />
                </svg>
              </div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100">
                {t('generator.other')}
              </h4>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      <div className="flex justify-end pt-2">
        <Button variant="secondary" onClick={() => generateOptions()}>
          <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          {t('generator.regenerate')}
        </Button>
      </div>
    </div>
  );

  // Field section component
  const renderFieldSection = () => {
    if (writingStyle.isGenerating) {
      return (
        <div className="flex flex-col items-center justify-center py-8">
          <AiProcessingAnimation type="content" />
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            {t('wizard.generatingWritingStyles')}
          </p>
        </div>
      );
    }

    if (writingStyle.viewMode === 'options' && writingStyle.options.length > 0) {
      return (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('wizard.chooseWritingStyle')}
          </h3>
          {renderOptions()}
        </div>
      );
    }

    if (writingStyle.viewMode === 'editing' || writingStyle.value) {
      return (
        <div className="space-y-3">
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
            {t('wizard.writingStyleLabel')}
          </label>
          <AiEnhancedTextarea
            value={writingStyle.value}
            onChange={(e) => {
              setWritingStyle((prev) => ({ ...prev, value: e.target.value }));
              setHasUnsavedChanges(true);
            }}
            placeholder={t('wizard.storytellerWritingStylePlaceholder')}
            minRows={3}
            maxRows={8}
            onMenuAction={handleWritingStyleAiAction}
            isLoading={writingStyle.isGenerating}
            hasContent={!!writingStyle.value?.trim()}
            showRephraseOptions={true}
            showChatOption={false}
            bookId={book.id}
          />
          {writingStyle.value && (
            <div className="flex gap-2 items-center">
              <Button type="button" variant="secondary" size="sm" onClick={() => handleClearField()}>
                {t('common.clear')}
              </Button>
            </div>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('wizard.storytellerWritingStyleHint')}
          </p>
        </div>
      );
    }

    // Initial state - show generate button
    return (
      <div className="space-y-3">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
          {t('wizard.writingStyleLabel')}
        </label>
        <div className="flex flex-col items-center py-4 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/30 dark:to-primary-800/30 flex items-center justify-center mb-3">
            <svg
              className="w-6 h-6 text-primary-600 dark:text-primary-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
              />
            </svg>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center max-w-xs mb-3">
            {t('wizard.writingStyleGenerateHint')}
          </p>
          <div className="flex gap-2 items-center">
            <AiGenerateDropdownButton
              onAction={handleWritingStyleAiAction}
              isLoading={writingStyle.isGenerating}
              hasContent={false}
              disabled={!hasSummary}
              showRephraseOptions={false}
              showChatOption={false}
              size="md"
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setWritingStyle((prev) => ({ ...prev, viewMode: 'editing' }))}
            >
              {t('wizard.writeOwn')}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  // No summary - show disabled state
  if (!hasSummary) {
    return (
      <div className="space-y-6">
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl flex items-start gap-3">
          <svg
            className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <p className="text-sm text-amber-800 dark:text-amber-200">
            {t('wizard.storytellerNeedsSummary')}
          </p>
        </div>
      </div>
    );
  }

  // Main view with writing style section only
  return (
    <div className="space-y-6">
      {/* Writing Style Section */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">{renderFieldSection()}</div>

      {error && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-sm text-red-500 text-center"
        >
          {error}
        </motion.p>
      )}

      {/* Next Button */}
      <div className="pt-4">
        <Button className="w-full" onClick={handleNext} isLoading={isSaving}>
          {t('wizard.next')}
        </Button>
      </div>
    </div>
  );
}
